import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import { i18next, site, dialogs, siteLocales } from '@yola/ws-sdk';
import { SubmitDialog } from '@yola/ws-ui';
import saveSite from 'src/js/modules/saving/helpers/save-site';
import getLanguageCodes from 'src/js/modules/website-settings/helpers/get-language-codes';
import performFolderDelete from '../helpers/perform-folder-delete';

const getCaptions = (areFoldersToDelete) => ({
  title: areFoldersToDelete
    ? i18next.t('Delete selected files?')
    : i18next.t('Delete selected pages?'),
  description: areFoldersToDelete ? (
    <>
      {i18next.t('This deletes the selected folders and pages.')}
      <br />
      {i18next.t('Pages inside folders will be deleted too.')}
      <br />
      {i18next.t('You can’t undo this.')}
    </>
  ) : (
    <>
      {i18next.t('This deletes the selected pages.')}
      <br />
      {i18next.t('You can’t undo this.')}
    </>
  ),
  submit: i18next.t('Submit'),
  cancel: i18next.t('Cancel'),
});

const BulkEditingDeleteContainer = ({
  pageIds,
  folderIds,
  itemType,
  bulkEditing,
  itemsCount,
  onDialogMainAction,
  onDialogCancel,
}) => {
  const dispatch = useDispatch();

  const captions = getCaptions(Boolean(folderIds.length));

  const handleCancel = () => {
    onDialogCancel({ itemType, bulkEditing, itemsCount });
    dispatch(dialogs.actions.show(dialogTypes.PAGE_MANAGER));
  };

  const handleSubmit = async () => {
    const pages = site.accessors.getPages();
    const activePageId = site.accessors.getActivePageId();
    const currentLocale = siteLocales.accessors.getCurrentLocale();

    if (pageIds.includes(activePageId)) {
      const homePage = pages.find(
        (page) => site.verifiers.isHomePage(page.path) && page.locale === currentLocale
      );

      await site.operations.setActivePage(homePage);
    }

    if (folderIds.length) {
      // eslint-disable-next-line no-restricted-syntax
      for (const folderId of folderIds) {
        const currentFolder = site.accessors.getFolders().find((folder) => folder.id === folderId);

        if (currentFolder) {
          // eslint-disable-next-line no-await-in-loop
          await performFolderDelete({ folderId, pages, activePageId });
        }
      }

      const updatedPageIds = site.accessors.getPages().map((page) => page.id);
      const restPageIdsToDelete = pageIds.filter((pageId) => updatedPageIds.includes(pageId));

      restPageIdsToDelete.forEach((pageId) => {
        dispatch(site.actions.deletePage(pageId, getLanguageCodes()));
      });
    } else {
      pageIds.forEach((pageId) => {
        dispatch(site.actions.deletePage(pageId, getLanguageCodes()));
      });
    }

    onDialogMainAction({ itemType, bulkEditing, itemsCount });
    saveSite();
    dispatch(dialogs.actions.show(dialogTypes.PAGE_MANAGER));
  };

  return (
    <SubmitDialog
      width={358}
      {...(!folderIds.length && { height: 200, minHeight: 200 })}
      captions={captions}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      onPressEnter={handleSubmit}
      onPressEsc={handleCancel}
    />
  );
};

BulkEditingDeleteContainer.propTypes = {
  pageIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  folderIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  itemType: PropTypes.string.isRequired,
  bulkEditing: PropTypes.bool.isRequired,
  itemsCount: PropTypes.number.isRequired,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
};

export default BulkEditingDeleteContainer;
